"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Building2,
  Sparkles,
  Target,
  Layers,
  Activity,
  TrendingUp,
  ArrowRight,
  Crown,
  Mic,
  CheckCircle2,
  Users,
  ShieldCheck,
  BookOpen,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { BentoBlock } from "@/lib/blocks/bento-block";
import { TabsBlock } from "@/lib/blocks/tabs-block";
import { CounterBlock } from "@/lib/blocks/counter-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const EVIDENCE = [
  { metric: "34→94", suffix: "%", label: "Policy Implementation", sub: "Improvement rate", accent: "emerald" },
  { metric: "42→89", suffix: "%", label: "Citizen Satisfaction", sub: "Elevation across deployments", accent: "cyan" },
  { metric: "120", suffix: "M", label: "Citizens Served", sub: "Single deployment scale", accent: "purple" },
  { metric: "72", suffix: "%", label: "Productivity Lift", sub: "Staff productivity gain", accent: "amber-gold" },
];

const DIMENSIONS = [
  { title: "Policy Intelligence", body: "Data-driven policy formation replacing intuition with empirical evidence. AI-powered policy impact modeling simulating thousands of permutations before implementation.", accent: "red" },
  { title: "Administrative Modernization", body: "End-to-end government digitization from paper-based workflows to automated, audit-trail-secured digital operations. Single-window service delivery.", accent: "purple" },
  { title: "Service Delivery Transformation", body: "End-to-end citizen journey mapping. Proactive service notification. Cross-agency coordination. Multi-channel delivery with accessibility compliance.", accent: "cyan" },
  { title: "Scheme Implementation Tracking", body: "Real-time monitoring from budget allocation through beneficiary verification to outcome measurement. Policy-to-impact traceability.", accent: "emerald" },
  { title: "Citizen Engagement & Feedback", body: "Digital platforms for citizen input. Participatory budgeting. Continuous sentiment analysis. Grievance redressal with AI-based triage.", accent: "amber-gold" },
];

const PILLARS = [
  { title: "Digital Infrastructure", body: "Connecting every level of government through a unified digital backbone. The foundation upon which everything else rests.", accent: "red" },
  { title: "Data Intelligence", body: "Revealing what is actually happening on the ground. Policy implementation status, citizen satisfaction, fraud patterns, budget utilization.", accent: "purple" },
  { title: "Process Reform", body: "Eliminating bureaucratic friction. Redundant approval cycles, departmental silos, accountability gaps that allow poor performance to persist.", accent: "cyan" },
  { title: "Citizen Engagement", body: "Ensuring services meet actual needs. Not the needs government assumes citizens have, but the needs citizens actually experience.", accent: "emerald" },
];

const STAGES = [
  { num: "01", title: "Assessment", body: "Comprehensive audit of existing governance infrastructure, process maturity, digital readiness, and citizen satisfaction baseline. The diagnostic phase." },
  { num: "02", title: "Design", body: "Architecture blueprint for the transformed governance operating model including technology, process, and organizational dimensions." },
  { num: "03", title: "Build", body: "Platform configuration, customization, integration, and deployment across all government departments and tiers. The operational infrastructure." },
  { num: "04", title: "Deploy", body: "Phased rollout across departments, regions, and tiers with continuous monitoring and rapid issue resolution. Citizen migration campaigns." },
  { num: "05", title: "Optimize", body: "Performance measurement, user feedback integration, process refinement, and continuous improvement cycles." },
  { num: "06", title: "Evolve", body: "Platform evolution through new service introduction, policy adaptation, technology upgrade, and scale expansion." },
];

const OUTCOMES = [
  { icon: Users, title: "Engagement Up 340%", body: "Citizen engagement increases by three hundred forty percent. Citizens who believe their government listens and delivers are citizens who support the institutions that govern them." },
  { icon: ShieldCheck, title: "Resolution Up 78%", body: "Grievance resolution improves by seventy-eight percent. AI-based triage categorizes and routes complaints. Automated escalation workflows ensure SLA compliance." },
  { icon: TrendingUp, title: "Service Adoption Up 410%", body: "Digital service adoption increases by four hundred ten percent. Service delivery cycles compress from six months to two and a half months." },
];

export default function GovernancePublicPolicyPage() {
  return (
    <>
      <PageHero
        icon={Building2}
        overline="Governance & Public Policy"
        overlineClassName="text-emerald"
        title={
          <>
            Converting Authority Into <span className="text-gradient-premium">Outcomes.</span>
          </>
        }
        subtitle="Governments hold authority. Citizens demand results. The distance between the two is where most governance fails — and where the governance transformation capability Dr. Jyoti Kush commands delivers. Eighteen countries. Over nine hundred million citizens served. Verified improvement in policy implementation, citizen satisfaction, and service delivery."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Expertise", href: "/expertise/" }, { label: "Governance & Public Policy" }]}
        badge="Engineered, not aspirational"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-5`}>
              <Sparkles className="size-3.5 text-emerald" />
              <span className="text-overline text-emerald">The Governance Challenge</span>
            </div>
            <h2 className="text-display-md mb-5">
              The distance between authority and outcomes is where most governments fail.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Elections are won on promises. Governing demands delivery. Only thirty-four percent of policies achieve full implementation. Sixty-six percent of public funds generate zero measurable impact. The average policy faces a two-point-seven-year delay from formation to delivery. Citizen satisfaction hovers at forty-two percent.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              These are not failures of intent. Most governments are staffed by dedicated professionals working within systems that were never designed for the scale and complexity they now face. The systems themselves are the constraint — and only systemic transformation can address systemic failure.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {EVIDENCE.map((e, i) => {
              const a = ACCENT[e.accent];
              return (
                <motion.div
                  key={e.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className={`text-display-sm font-bold ${a.text} leading-none mb-2`}>{e.metric}{e.suffix}</div>
                  <div className="text-caption font-bold uppercase tracking-wider mb-1">{e.label}</div>
                  <div className="text-caption text-muted-foreground">{e.sub}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Layers className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Five-Dimensional Framework</span>
          </div>
          <h2 className="text-display-md mb-4">The architecture of comprehensive transformation.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each dimension essential, each dimension dependent on the others, none sufficient in isolation. The framework was forged through years of operating across eighteen countries.
          </p>
        </div>
        <div className="space-y-3">
          {DIMENSIONS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr] gap-5 items-center">
                  <div className={`size-14 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg`}>
                    <span className="text-body-md font-bold text-white">{`0${i + 1}`}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-1 ${a.text}`}>{d.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Four Non-Negotiable Pillars</span>
          </div>
          <h2 className="text-display-md mb-4">The foundation of every governance transformation.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Remove any single pillar and the system collapses into the same fragmented, ineffective approach that governments already employ.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`text-display-sm font-bold ${a.text} opacity-30 leading-none mb-3`}>{`0${i + 1}`}</div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{p.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Activity className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Six-Stage Delivery Methodology</span>
          </div>
          <h2 className="text-display-md mb-4">How governance transformation engagements execute.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every stage includes defined deliverables, quality gates, and stakeholder sign-offs. No transformation proceeds without meeting objective readiness criteria.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {STAGES.map((s, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`group relative glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-caption font-bold ${a.text} opacity-50 mb-2`}>Stage {s.num}</div>
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{s.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Target className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Verified Outcomes</span>
          </div>
          <h2 className="text-display-md mb-4">Good governance is not just a virtue. It is an electoral advantage.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Citizens who believe their government listens, responds, and delivers are citizens who support the institutions that govern them.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {OUTCOMES.map((o, i) => {
            const a = ACCENT[["emerald", "cyan", "amber-gold"][i]];
            return (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
              >
                <div className="size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg">
                  <o.icon className="size-6 text-white" />
                </div>
                <h3 className={`text-heading-md mb-3 ${a.text}`}>{o.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{o.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20" id="governance-bento" ariaLabel="Governance Frameworks Bento">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <BookOpen className="size-3.5 text-denim" />
            <span className="text-overline text-denim">The Governance Frameworks Bento</span>
          </div>
          <h2 className="text-display-md mb-4">Eight frameworks. One transformation system.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The signature compilation. Each framework governs a discrete domain of governance work. The frameworks are not parallel — they interlock, each providing the structural support for the others.
          </p>
        </div>
        <BentoBlock
          items={[
            { title: "Policy Intelligence Framework", body: "Data-driven policy formation replacing intuition with empirical evidence. AI-powered policy impact modeling that simulates thousands of permutations before implementation.", icon: <Target className="size-5" />, accent: "red", span: "lg" },
            { title: "Administrative Modernization", body: "End-to-end government digitization. Single-window service delivery. Audit-trail-secured digital operations replacing paper-based workflows.", icon: <Building2 className="size-5" />, accent: "purple", span: "sm" },
            { title: "Service Delivery Transformation", body: "End-to-end citizen journey mapping. Proactive notification. Cross-agency coordination. Multi-channel delivery with accessibility compliance.", icon: <Activity className="size-5" />, accent: "cyan", span: "sm" },
            { title: "Scheme Implementation Tracking", body: "Real-time monitoring from budget allocation through beneficiary verification to outcome measurement. Policy-to-impact traceability at the citizen level.", icon: <CheckCircle2 className="size-5" />, accent: "emerald", span: "sm" },
            { title: "Citizen Engagement & Feedback", body: "Digital platforms for citizen input. Participatory budgeting. Continuous sentiment analysis. Grievance redressal with AI-based triage.", icon: <Users className="size-5" />, accent: "amber-gold", span: "sm" },
            { title: "Digital Identity & Authentication", body: "Aadhaar-aligned identity verification. Biometric authentication. Cross-agency interoperability. The secure identity layer that enables every other framework.", icon: <ShieldCheck className="size-5" />, accent: "denim", span: "wide" },
            { title: "Fraud Detection & Prevention", body: "Pattern recognition across entitlement programs. Beneficiary deduplication. Anomaly detection. Prevention of the leakages that erode public trust.", icon: <ShieldCheck className="size-5" />, accent: "coral", span: "sm" },
            { title: "Sovereignty Management", body: "National data residency. Citizen data sovereignty. Cross-border data flow governance. The framework that ensures transformation respects sovereignty.", icon: <Sparkles className="size-5" />, accent: "lime", span: "sm" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Activity className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Application Modalities</span>
          </div>
          <h2 className="text-display-md mb-4">How governance transformation is delivered.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Three modalities, each calibrated to the stakes and the scale of the engagement. The modality is selected based on diagnostic — not preference.
          </p>
        </div>
        <TabsBlock
          defaultValue="national"
          tabs={[
            {
              value: "national",
              label: "National Governments",
              content: (
                <div className="space-y-4">
                  <h3 className="text-heading-md text-emerald">National-Scale Governance Transformation</h3>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    End-to-end governance modernization at national scale. The deployment model designed for governments that require comprehensive transformation across policy, administration, service delivery, and citizen engagement. GOVERN G5 with one hundred twenty-seven modules across nine government function categories scales to one hundred twenty million citizens in a single deployment.
                  </p>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Federal government ROI: six hundred twenty percent. Policy implementation rates improve from thirty-four percent to ninety-four percent. Citizen satisfaction improves from forty-two percent to eighty-nine percent. The transformation is not theoretical. It is verified.
                  </p>
                </div>
              ),
            },
            {
              value: "regional",
              label: "State & Regional",
              content: (
                <div className="space-y-4">
                  <h3 className="text-heading-md text-emerald">State & Regional Deployments</h3>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Sub-national governance transformation for states, provinces, and regional administrations. The deployment model designed for jurisdictions with autonomous policy authority that require transformation within their specific constitutional and administrative context. CEREBRAS P5 with one hundred twenty-nine capabilities across five governance pillars — Policy, Police, Pulse, Power, Public.
                  </p>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Deployed at two hundred-plus organizations including fifty-plus Fortune 500 companies. Ninety-one to ninety-six percent public sentiment prediction accuracy. Staff productivity improves by seventy-two percent. The methodology adapts to constitutional structure.
                  </p>
                </div>
              ),
            },
            {
              value: "municipal",
              label: "Municipal & Local",
              content: (
                <div className="space-y-4">
                  <h3 className="text-heading-md text-emerald">Municipal & Local Government Transformation</h3>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    City and municipal transformation — the most citizen-facing layer of governance. Service delivery, complaint resolution, infrastructure management, and local economic development. The deployment model designed for mayors and municipal leadership that require immediate, visible improvements in citizen experience.
                  </p>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Citizen engagement increases by three hundred forty percent. Grievance resolution improves by seventy-eight percent. Digital service adoption increases by four hundred ten percent. The transformation is local, visible, and measurable.
                  </p>
                </div>
              ),
            },
            {
              value: "institutional",
              label: "Institutional",
              content: (
                <div className="space-y-4">
                  <h3 className="text-heading-md text-emerald">Institutional & Public Sector Organizations</h3>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Public sector institutions — universities, healthcare systems, public enterprises, regulatory bodies. The deployment model designed for organizations with public mandate and private-sector operating discipline. The methodology adapts the governance transformation playbook to the institutional context.
                  </p>
                  <p className="text-body-md text-platinum-300/85 leading-relaxed">
                    Multi-year engagements with continuous measurement. Service quality improves. Operational efficiency lifts. Citizen trust in the institution rises. The transformation is durable and outcome-verified.
                  </p>
                </div>
              ),
            },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Verified Performance</span>
          </div>
          <h2 className="text-display-md mb-4">The record of governance transformation.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Every metric traces to operational data. The numbers represent outcomes from real deployments where the margin for error is national and the cost of failure is public trust.
          </p>
        </div>
        <CounterBlock
          columns={4}
          stats={[
            { value: 34, suffix: "→94", prefix: "", label: "Policy Implementation %", description: "Improvement from baseline to deployment", accent: "emerald" },
            { value: 42, suffix: "→89", prefix: "", label: "Citizen Satisfaction %", description: "Elevation across deployments", accent: "cyan" },
            { value: 120, suffix: "M", label: "Citizens Served", description: "Single deployment scale", accent: "purple" },
            { value: 72, suffix: "%", label: "Productivity Lift", description: "Staff productivity gain", accent: "amber-gold" },
            { value: 620, suffix: "%", label: "Federal Government ROI", description: "Verified return on engagement investment", accent: "red" },
            { value: 200, suffix: "+", label: "Organizations Deployed", description: "Including fifty-plus Fortune 500 companies", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Sparkles className="size-10 text-emerald mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Engage governance transformation.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Sovereign-grade governance transformation for governments, public institutions, and international organizations. Every engagement governed by the six-stage methodology. Every outcome verified.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin an Engagement
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/expertise/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Layers className="size-4" />
                All Expertise Domains
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Expertise Hub", description: "The complete map of fifteen expertise domains. One integrated operating system.", href: "/expertise/", icon: Building2, accent: "emerald" },
          { title: "Digital Transformation", description: "Engineering the digital future. The operating system for statecraft at national scale.", href: "/expertise/digital-transformation/", icon: Layers, accent: "cyan" },
          { title: "AI, Technology & The Future", description: "Governing intelligence at sovereign scale. The AI architecture the future demands.", href: "/expertise/ai-technology-future/", icon: TrendingUp, accent: "purple" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator. A decade of sovereign-scale leadership.", href: "/about/", icon: Crown, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
